import React, { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { runJob } from "@/lib/jobs";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import {
  Sparkles, Video, Megaphone, Layers, Presentation, Loader2, Copy, Check,
  ArrowRight, Lock, Wand2, ChevronRight, Download,
} from "lucide-react";
import { toast } from "sonner";

/** Wikifi CREATE STUDIO — the flagship pro-content generator.
 *  4 workflow templates; every one uses the real backend Create endpoint
 *  which orchestrates GPT-5.4 (copy) + Nano Banana / GPT Image 1 (visuals).
 */

const TEMPLATES = [
  {
    id: "youtube_video",
    name: "YouTube Video",
    tagline: "Script, thumbnail concept, title A/B set, description with SEO tags.",
    icon: Video,
    accent: "#FF0000",
    fields: [
      { key: "topic", label: "What's the video about?", type: "textarea",
        placeholder: "e.g. Explain OpenAI Sora 2 in under 8 minutes for creators" },
      { key: "audience", label: "Who is it for?", type: "text",
        placeholder: "e.g. Aspiring content creators, ages 22-35" },
      { key: "duration", label: "Target length", type: "select",
        options: ["Under 60s (Short)", "3–5 min", "8–12 min", "15–20 min"] },
      { key: "vibe", label: "Vibe / style", type: "select",
        options: ["Fast & punchy", "Cinematic & moody", "Explainer & calm", "Comedic"] },
    ],
    outputs: ["Hook (0-5s)", "Full script outline", "3 title options", "Thumbnail concept",
              "SEO description", "10 chapter timestamps", "End-screen CTA"],
  },
  {
    id: "ad_campaign",
    name: "Ad Campaign",
    tagline: "Multi-format ads: hero image, 3 headlines, primary text, and landing hero.",
    icon: Megaphone,
    accent: "#F97316",
    fields: [
      { key: "product", label: "What are you advertising?", type: "textarea",
        placeholder: "e.g. Wikifi All Access — $9.99/mo. Unlimited AI Create Studio + full prompt library." },
      { key: "audience", label: "Target audience", type: "text",
        placeholder: "e.g. Small business owners, solopreneurs on Meta" },
      { key: "channel", label: "Primary channel", type: "select",
        options: ["Meta (FB / IG)", "TikTok", "LinkedIn", "Google Search", "YouTube pre-roll"] },
      { key: "goal", label: "Primary goal", type: "select",
        options: ["Sign-ups", "Purchases", "Waitlist", "Brand awareness"] },
    ],
    outputs: ["Hero image concept", "3 headline variants (A/B/C)", "Primary ad copy",
              "CTA button copy", "Landing hero + subhead", "5 hashtags"],
  },
  {
    id: "social_pack",
    name: "Social Pack",
    tagline: "One idea → LinkedIn post, X thread, Instagram caption, and TikTok hook.",
    icon: Layers,
    accent: "#0EA5E9",
    fields: [
      { key: "topic", label: "What's the story?", type: "textarea",
        placeholder: "e.g. We just added Sora 2 to Wikifi Create Studio" },
      { key: "voice", label: "Your voice", type: "select",
        options: ["Warm & personal", "Sharp & confident", "Playful & witty", "Deeply expert"] },
      { key: "cta", label: "Primary CTA", type: "text",
        placeholder: "e.g. Try free at wikificial.com" },
    ],
    outputs: ["LinkedIn post (long-form)", "X thread (5-7 tweets)", "IG caption + hashtags",
              "TikTok hook + on-screen text", "3 reply-worthy hot takes"],
  },
  {
    id: "pitch_deck",
    name: "Pitch Deck",
    tagline: "Structured 10-slide outline with speaker notes and slide-by-slide visuals.",
    icon: Presentation,
    accent: "#8B5CF6",
    fields: [
      { key: "product", label: "What are you pitching?", type: "textarea",
        placeholder: "e.g. Wikifi — Every AI. The encyclopedia + Create Studio in one." },
      { key: "audience", label: "Pitching to whom?", type: "select",
        options: ["Seed VCs", "Series A VCs", "Angel investors", "Enterprise buyers", "Internal leadership"] },
      { key: "ask", label: "Your ask", type: "text",
        placeholder: "e.g. Raising $1.5M seed at $10M cap" },
    ],
    outputs: ["10-slide outline", "Speaker notes per slide", "Visual concept per slide",
              "One-liner elevator pitch", "3 objection responses"],
  },
];

const RESULT_ICONS = { image: "🎨", copy: "✍️", script: "🎬", social: "📱", plan: "📋" };

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1500);
      }}
      className="text-[11px] flex items-center gap-1 text-[#5B6478] hover:text-[#0B1220] font-semibold"
      data-testid={`copy-${label}`}
    >
      {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
    </button>
  );
}

function ResultBlock({ heading, body, index }) {
  return (
    <div className="border border-[#E3E8F0] rounded-xl p-4 bg-white" data-testid={`result-block-${index}`}>
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="font-semibold text-sm text-[#0B1220]">{heading}</h4>
        <CopyButton text={body} label={`block-${index}`} />
      </div>
      <div className="text-[13px] leading-relaxed whitespace-pre-wrap text-[#0B1220]/85">{body}</div>
    </div>
  );
}

export default function CreateStudio() {
  const [templateId, setTemplateId] = useState(TEMPLATES[0].id);
  const [inputs, setInputs] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [quotaLeft, setQuotaLeft] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const template = TEMPLATES.find(t => t.id === templateId);
  const tier = user?.is_admin
    ? "admin"
    : user?.is_pro
      ? "all_access"
      : user?.is_creator
        ? "creator"
        : user?.is_curious
          ? "curious"
          : "free";

  useEffect(() => {
    setInputs({}); setResult(null); setError(null);
  }, [templateId]);

  useEffect(() => {
    api.get("/create/quota").then(r => setQuotaLeft(r.data)).catch(() => {});
  }, [result]);

  const setField = (key, val) => setInputs(s => ({ ...s, [key]: val }));

  const canRun = template.fields.every(f => (inputs[f.key] || "").trim().length > 0);

  const run = async () => {
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      // Async job pattern — kickoff returns immediately, we poll for the result.
      // Text + image generation can take 30-90s, well past any CF/gateway ceiling.
      const data = await runJob("create", {
        template: templateId,
        inputs,
      });
      setResult(data);
    } catch (err) {
      // runJob throws Error with .message = server-provided detail
      // Detect the kickoff auth/quota errors (thrown before job starts) vs runtime errors.
      const msg = err?.message || "Something went wrong. Try again.";
      if (msg.toLowerCase().includes("sign in") || msg.toLowerCase().includes("plan")) {
        setError({ kind: "paywall", msg });
      } else if (msg.toLowerCase().includes("used all") || msg.toLowerCase().includes("limit")) {
        setError({ kind: "quota", msg });
      } else {
        setError({ kind: "error", msg });
      }
    } finally { setRunning(false); }
  };

  const rightPanel = (
    <>
      <div className="curio-card p-5" data-testid="create-quota-panel">
        <div className="text-[10px] uppercase tracking-widest font-bold text-coral mb-2">Your plan</div>
        <div className="text-lg font-semibold capitalize">{tier === "free" ? "Free" : tier.replace("_", " ")}</div>
        {quotaLeft && (
          <div className="mt-3 text-sm">
            <div className="text-[#5B6478]">Create runs this month</div>
            <div className="text-2xl font-bold">{quotaLeft.remaining === -1 ? "Unlimited" : quotaLeft.remaining}
              {quotaLeft.remaining !== -1 && <span className="text-sm text-[#5B6478] font-normal"> / {quotaLeft.monthly_cap}</span>}
            </div>
          </div>
        )}
        {tier === "free" && (
          <Link to="/pricing" className="btn-coral text-xs w-full justify-center mt-3">Upgrade to unlock</Link>
        )}
      </div>

      <div className="curio-card p-5">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478] mb-2">Studio tips</div>
        <ul className="text-sm space-y-2">
          <li className="flex gap-2"><ChevronRight size={14} className="text-coral mt-0.5"/>Be specific: audience + tone + one concrete goal.</li>
          <li className="flex gap-2"><ChevronRight size={14} className="text-coral mt-0.5"/>Include numbers, names, and constraints — the model uses them.</li>
          <li className="flex gap-2"><ChevronRight size={14} className="text-coral mt-0.5"/>You can regenerate any block with a follow-up prompt.</li>
        </ul>
      </div>
    </>
  );

  return (
    <Layout rightPanel={rightPanel}>
      <PageHero
        testId="create-hero"
        eyebrow="Wikifi Create Studio"
        title="Create like you've got a full studio behind you."
        sub="One brief. Full production. Wikifi orchestrates the best AI tools in the world to draft your video, your ad, your deck, your social pack — polished and publication-ready in under 60 seconds."
      />

      {/* Template picker */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="template-picker">
        {TEMPLATES.map(t => {
          const Icon = t.icon;
          const active = t.id === templateId;
          return (
            <button
              key={t.id}
              onClick={() => setTemplateId(t.id)}
              className={`text-left p-4 rounded-2xl border-2 transition-all ${
                active ? "border-[#0B1220] bg-white shadow-sm" : "border-[#E3E8F0] bg-white hover:border-[#0B1220]/40"
              }`}
              data-testid={`template-${t.id}`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 text-white"
                   style={{ background: t.accent }}>
                <Icon size={18} />
              </div>
              <div className="font-display text-lg font-semibold leading-tight">{t.name}</div>
              <div className="text-xs text-[#5B6478] mt-1 leading-snug">{t.tagline}</div>
            </button>
          );
        })}
      </div>

      {/* Brief form */}
      <section className="curio-card p-6 mb-6" data-testid="brief-form">
        <div className="flex items-center gap-2 mb-4">
          <Wand2 size={16} className="text-coral" />
          <h2 className="font-display text-2xl font-semibold">Your brief</h2>
        </div>
        <div className="space-y-4">
          {template.fields.map(f => (
            <div key={f.key}>
              <label className="text-xs uppercase tracking-widest font-bold text-[#5B6478] block mb-1.5">
                {f.label}
              </label>
              {f.type === "textarea" ? (
                <textarea
                  value={inputs[f.key] || ""}
                  onChange={e => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full min-h-[80px] border border-[#E3E8F0] rounded-xl p-3 text-sm focus:outline-none focus:border-[#0B1220]"
                  data-testid={`field-${f.key}`}
                />
              ) : f.type === "select" ? (
                <select
                  value={inputs[f.key] || ""}
                  onChange={e => setField(f.key, e.target.value)}
                  className="w-full border border-[#E3E8F0] rounded-xl p-2.5 text-sm bg-white focus:outline-none focus:border-[#0B1220]"
                  data-testid={`field-${f.key}`}
                >
                  <option value="">Pick one…</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type="text"
                  value={inputs[f.key] || ""}
                  onChange={e => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full border border-[#E3E8F0] rounded-xl p-2.5 text-sm focus:outline-none focus:border-[#0B1220]"
                  data-testid={`field-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-6 gap-3">
          <div className="text-xs text-[#5B6478]">
            {template.outputs.length} outputs · uses GPT-5.4 + Nano Banana visuals
          </div>
          <button
            onClick={run}
            disabled={!canRun || running}
            className="btn-coral text-sm disabled:opacity-50"
            data-testid="create-run-btn"
          >
            {running ? <><Loader2 size={14} className="animate-spin"/> Producing…</> :
              <><Sparkles size={14}/> Create · uses 1 credit</>}
          </button>
        </div>
      </section>

      {/* Errors / paywall */}
      {error?.kind === "paywall" && (
        <div className="curio-card p-6 mb-6 border-2 border-coral" data-testid="create-paywall">
          <div className="flex items-center gap-3">
            <Lock size={20} className="text-coral"/>
            <div className="flex-1">
              <div className="font-semibold">{error.msg}</div>
              <div className="text-sm text-[#5B6478]">Curious ($2.99/mo) unlocks 15 Creates/mo. Creator ($4.99) unlocks unlimited.</div>
            </div>
            <Link to="/pricing" className="btn-coral text-sm" data-testid="create-upgrade-btn">See plans <ArrowRight size={12}/></Link>
          </div>
        </div>
      )}
      {error?.kind === "quota" && (
        <div className="curio-card p-6 mb-6 border-2 border-coral">
          <div className="font-semibold mb-1">You've used every Create for this month.</div>
          <div className="text-sm text-[#5B6478] mb-3">{error.msg}</div>
          <Link to="/pricing" className="btn-coral text-sm inline-flex">Upgrade to unlimited <ArrowRight size={12}/></Link>
        </div>
      )}
      {error?.kind === "error" && (
        <div className="curio-card p-4 mb-6 border-2 border-rose-200 text-rose-700 text-sm">
          {error.msg}
        </div>
      )}

      {/* Result */}
      {result && (
        <section className="mb-8" data-testid="create-result">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-coral">Delivered</div>
              <h2 className="font-display text-3xl font-semibold">Your production kit.</h2>
            </div>
            <button onClick={() => {
              const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `wikificial-${templateId}-${Date.now()}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }} className="text-sm flex items-center gap-1.5 text-[#5B6478] hover:text-[#0B1220]" data-testid="download-result-btn">
              <Download size={14}/> Export JSON
            </button>
          </div>

          {result.hero_image_url && (
            <div className="mb-4 rounded-2xl overflow-hidden border border-[#E3E8F0]">
              <img src={result.hero_image_url} alt="Hero concept" className="w-full max-h-[480px] object-cover"
                   data-testid="result-hero-image"/>
              <div className="p-3 bg-[#F7F9FC] text-xs text-[#5B6478]">Hero visual concept · generated by Nano Banana</div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="result-blocks">
            {result.blocks?.map((b, i) => <ResultBlock key={`${b.heading}-${i}`} heading={b.heading} body={b.body} index={i} />)}
          </div>

          {result.notes && (
            <div className="mt-4 text-xs text-[#5B6478] italic">
              {result.notes}
            </div>
          )}
        </section>
      )}

      {!result && !running && !error && (
        <div className="text-center py-10 text-sm text-[#5B6478] border-2 border-dashed border-[#E3E8F0] rounded-2xl"
             data-testid="create-empty-state">
          Fill in the brief above. Your production kit will appear here in under a minute.
        </div>
      )}
    </Layout>
  );
}
